import { Compass, GraduationCap, Code } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function Framework() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const pillars = [
    {
      icon: Compass,
      title: "Strategy",
      description: "We identify high-impact AI opportunities, map transformation roadmaps, and align leadership on priorities.",
      metrics: "Clarity on where AI drives ROI",
      color: "bronze",
    },
    {
      icon: GraduationCap,
      title: "Education",
      description: "We train and empower teams to adopt AI tools confidently and effectively.",
      metrics: "Confidence across your organization",
      color: "warm-gold",
    },
    {
      icon: Code,
      title: "Development",
      description: "We build intelligent systems that automate and optimize your core workflows.",
      metrics: "Measurable impact on operations",
      color: "cyan",
    },
  ];

  return (
    <section id="framework" className="py-20 md:py-32 px-6 bg-gradient-to-b from-deep-navy to-medium-navy">
      <div ref={ref} className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 scroll-fade-in ${isVisible ? 'visible' : ''}`}>
          <div className="inline-block px-4 py-1 rounded-full border border-cyan/30 bg-cyan/5 mb-6 text-xs text-cyan font-bold uppercase tracking-wider">
            The ArqeraAI Framework
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Architects of Intelligent Systems
          </h2>
          <p className="text-lg md:text-xl text-light-gray/80 max-w-3xl mx-auto">
            We guide your transformation through three integrated pillars — from strategic clarity to operational excellence.
          </p>
        </div>

        {/* Three Pillars Grid */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            const colorClasses = {
              bronze: "bg-bronze/10 text-bronze border-bronze/30",
              "warm-gold": "bg-warm-gold/10 text-warm-gold border-warm-gold/30",
              cyan: "bg-cyan/10 text-cyan border-cyan/30",
            };
            
            return (
              <div
                key={index}
                className={`bg-dark-card rounded-xl border border-border/20 p-8 hover:border-bronze/40 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-bronze/10 hover:scale-[1.02] relative overflow-hidden group scroll-scale-in ${isVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-bronze/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-lg ${colorClasses[pillar.color as keyof typeof colorClasses].split(' ')[0]} flex items-center justify-center mb-6`}>
                    <Icon className={`w-8 h-8 ${colorClasses[pillar.color as keyof typeof colorClasses].split(' ')[1]}`} />
                  </div>

                  {/* Number Badge */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-bronze/10 flex items-center justify-center">
                    <span className="text-xl font-bold text-bronze">
                      {index + 1}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    {pillar.title}
                  </h3>

                  {/* Description */}
                  <p className="text-base text-light-gray/80 leading-relaxed mb-6">
                    {pillar.description}
                  </p>

                  {/* Metrics */}
                  <div className={`pt-4 border-t ${colorClasses[pillar.color as keyof typeof colorClasses].split(' ')[2]}`}>
                    <p className={`text-sm font-semibold ${colorClasses[pillar.color as keyof typeof colorClasses].split(' ')[1]}`}>
                      {pillar.metrics}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Tagline */}
        <div className="text-center mt-16">
          <p className="text-xl text-cyan font-semibold">
            Where Strategy Meets AI Execution
          </p>
        </div>
      </div>
    </section>
  );
}
